import { useCallback, useEffect, useRef, useState } from 'react';
import { useResizeObserver } from 'use-resize-observer';

export function useTableContainer() {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [tableContainerElement, setTableContainerElement] =
    useState<HTMLDivElement | null>(null);

  const setTableContainerRef = useCallback((node: HTMLDivElement | null) => {
    tableContainerRef.current = node;
    setTableContainerElement(node);
  }, []);

  const handleTableScrollPosition = useCallback((): void => {
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);

    rafRef.current = window.requestAnimationFrame(() => {
      const target = tableContainerRef.current;
      if (!target) return;

      const scrollWidth = target.scrollWidth - target.clientWidth;
      target.dataset.scrolled = target.scrollLeft > 0 ? 'true' : 'false';
      target.dataset.scrolledToEnd =
        Math.round(target.scrollLeft) >= scrollWidth ? 'true' : 'false';
      rafRef.current = null;
    });
  }, []);

  useResizeObserver({
    ref: tableContainerElement,
    onResize: handleTableScrollPosition,
  });

  useEffect(() => {
    if (!tableContainerElement) return;

    tableContainerElement.addEventListener(
      'scroll',
      handleTableScrollPosition,
      {
        passive: true,
      },
    );

    return () => {
      tableContainerElement.removeEventListener(
        'scroll',
        handleTableScrollPosition,
      );
    };
  }, [handleTableScrollPosition, tableContainerElement]);

  return { setTableContainerRef, tableContainerElement };
}
